import { assert, definePrivateProps, clone, isFunction, noop } from '@logosdx/utils';
import { arrOfMatchingValues, InternalEvent, InternalEvs, makeEventTracer, rgxStrToRgx, validateEvent, validateListener } from './helpers.mjs';
import { EventGenerator, DeferredEvent } from './generator.mjs';
import { EventQueue } from './index.mjs';
export class ObserverEngine {
    #listenerMap = new Map();
    #rgxListenerMap = new Map();
    #internalListener = new EventTarget();
    #emitValidator;
    #spy;
    // Hidden property for holding the spy function
    // when debugging is enabled. Used to restore the
    // original spy function when debugging is disabled.
    #__spy;
    // Instance-level AbortController for cleanup
    #instanceController = new AbortController();
    #instanceSignal;
    constructor(options){
        // Validate option if exists
        if (options) {
            assert(!options.name || typeof options.name === 'string', 'Observable options.name must be a string', TypeError);
            assert(!options.spy || typeof options.spy === 'function', 'Observable options.spy must be a function', TypeError);
            assert(!options.emitValidator || typeof options.emitValidator === 'function', 'Observable options.emitValidator must be a function', TypeError);
            this.#spy = options.spy;
        }
        // Setup instance-level signal
        // We use the internal controller's signal directly and listen separately
        // to user signals to avoid AbortSignal.any() memory retention issues
        this.#instanceSignal = this.#instanceController.signal;
        const userSignal = options?.signal;
        // Handler that clears all listeners when any abort occurs
        const clearOnAbort = ()=>this.clear();
        // Listen for internal abort
        this.#instanceSignal.addEventListener('abort', clearOnAbort, {
            once: true
        });
        // If user provided a signal, also listen on that
        if (userSignal) {
            userSignal.addEventListener('abort', clearOnAbort, {
                once: true
            });
        }
        // Make these functions non-enumerable
        definePrivateProps(this, {
            on: this.on,
            once: this.once,
            emit: this.emit,
            off: this.off,
            observe: this.observe,
            debug: this.debug,
            $has: this.$has,
            $facts: this.$facts,
            $internals: this.$internals,
            name: options?.name || Math.random().toString(36).substring(7)
        });
        this.#emitValidator = options?.emitValidator;
    }
    name;
    /**
     * Returns facts about the the internal state of the observable instance.
     */ $facts() {
        const listenKeys = [
            ...this.#listenerMap.keys()
        ];
        const rgxKeys = [
            ...this.#rgxListenerMap.keys()
        ];
        const listenerCounts = Object.fromEntries([
            listenKeys.map((k)=>[
                    k,
                    this.#listenerMap.get(k)?.size
                ]),
            rgxKeys.map((k)=>[
                    k,
                    this.#rgxListenerMap.get(k)?.size
                ])
        ].flat());
        return {
            listeners: listenKeys,
            rgxListeners: rgxKeys,
            listenerCounts,
            hasSpy: !!this.#spy
        };
    }
    /**
	 * The internals of the observable instance.
     *
     * NOTE: Do not use this to try to meddle with the
     * internals of the observable instance. This is for
     * debugging purposes only.
	 */ $internals() {
        return {
            listenerMap: clone(this.#listenerMap),
            rgxListenerMap: clone(this.#rgxListenerMap),
            internalListener: clone(this.#internalListener),
            name: this.name,
            spy: this.#spy
        };
    }
    $has(event) {
        if (event instanceof RegExp) {
            return this.#rgxListenerMap.has(event.toString());
        }
        return this.#listenerMap.has(event) || this.#rgxListenerMap.has(event);
    }
    #currentSpy(...args) {
        if (this.#spy) {
            this.#spy.apply(this, args);
        }
    }
    /**
     * Enables or disables debugging for the observable instance.
     * Works in conjunction with your spy function. Provides a
     * stack trace of events that are triggered, listened to, and
     * cleaned up.
     *
     * @param on Whether to enable or disable debugging
     */ debug(on = true) {
        const original = this.#spy;
        const spy = (ev)=>{
            const { event, fn, data, listener } = ev;
            console.info(makeEventTracer(event, fn, data || listener));
            original && original(ev);
        };
        if (on) {
            this.#spy = spy;
            this.#__spy = original;
            return;
        }
        this.#spy = this.#__spy;
    }
    /**
     * Sets the spy function for the observable instance.
     * @param spy The spy function to set
     * @param force Whether to force the spy function to be set even if one is already set
     */ spy(spy, force = false) {
        assert(isFunction(spy), 'spy must be a function', TypeError);
        assert(force || this.#spy === undefined, 'you already have a spy function set', TypeError);
        this.#spy = spy;
    }
    /**
     * Observes given component as an extension of this observable instance.
     * @param component Component to wrap events around
     * @param options Optional configuration including signal for cleanup
     *
     * @example
     *
     * const obs = new ObserverEngine();
     *
     * const modal = {};
     *
     * obs.observe(modal);
     *
     * modal.on('modal-open', () => {});
     *
     * obs.trigger('modal-open'); // opens modal
     * modal.trigger('modal-open'); // opens modal
     *
     * modal.cleanup(); // clears all event listeners
     */ observe(component, options) {
        const self = this;
        // Check if signal is already aborted - set up noop child
        const userSignal = options?.signal;
        if (userSignal?.aborted) {
            definePrivateProps(component, {
                on: noop,
                once: noop,
                off: noop,
                emit: noop,
                clear: noop,
                cleanup: noop
            });
            return component;
        }
        const track = new Map;
        const trackListener = (event, fn)=>{
            const _set = track.get(event) || new Set;
            _set.add(fn);
            track.set(event, _set);
            return fn;
        };
        // Handle removing all callbacks from this instance related to child observable.
        // Only all of the child instance's callbacks should be removed.
        const rmAll = ()=>{
            for (const entry of track.entries()){
                const [ev, _set] = entry;
                // For each function, remove listener from observer
                [
                    ..._set
                ].forEach((fn)=>self.off(ev, fn));
                // Clear the tracker
                track.set(ev, new Set());
            }
        };
        const afterOff = (_event)=>{
            const [event, callback] = _event.data;
            const _set = track.get(event);
            _set?.delete(callback);
        };
        this.#internalListener.addEventListener(InternalEvs.off, afterOff);
        this.#internalListener.addEventListener(InternalEvs.clear, rmAll);
        const on = (ev, fn)=>{
            return self.on(ev, trackListener(ev, fn));
        };
        const once = (ev, fn)=>{
            return self.once(ev, trackListener(ev, fn));
        };
        const off = (ev, fn)=>{
            self.off(ev, fn);
        };
        const emit = (ev, data)=>{
            return self.emit(ev, data);
        };
        const clear = ()=>rmAll();
        // Mutable controller for managing abort listener lifecycle
        let observeAbortCtrl;
        const cleanup = ()=>{
            rmAll();
            self.#internalListener.removeEventListener(InternalEvs.off, afterOff);
            self.#internalListener.removeEventListener(InternalEvs.clear, rmAll);
            observeAbortCtrl?.abort();
        };
        // Compose signal with instance signal for automatic cleanup
        if (userSignal) {
            observeAbortCtrl = new AbortController();
            const composedSignal = AbortSignal.any([
                this.#instanceSignal,
                userSignal
            ]);
            composedSignal.addEventListener('abort', cleanup, {
                signal: observeAbortCtrl.signal
            });
        }
        definePrivateProps(component, {
            on,
            once,
            off,
            emit,
            clear,
            cleanup
        });
        return component;
    }
    /**
     * Gets information about the event, whether it's a regex or not,
     * the event name, and the regex itself.
     */ #eventInfo(event) {
        const isRgx = event.constructor === RegExp;
        const eventName = event.toString();
        return {
            isRgx,
            eventName,
            rgx: event
        };
    }
    /**
     * Returns all event names that match the regex
     */ #withRgxMatchKeys(rgx) {
        return [
            ...this.#listenerMap.keys()
        ].filter((k)=>rgx.test(k));
    }
    /**
     * Returns all regexes that match the key
     */ #withKeyMatchRgx(key) {
        return [
            ...this.#rgxListenerMap.keys()
        ].filter((s)=>rgxStrToRgx(s).test(key)).flat();
    }
    /**
     * Returns all event names that match the regex
     */ #matchStr(rgx) {
        const events = this.#withRgxMatchKeys(rgx);
        return arrOfMatchingValues(events, this.#listenerMap);
    }
    /**
     * Returns all regexes that match the key
     */ #matchRgx(str) {
        const events = this.#withKeyMatchRgx(str);
        return arrOfMatchingValues(events, this.#rgxListenerMap);
    }
    /**
     * Listen for an event
     * @param event
     * @param listenerOrOptions - Either a callback function or options object
     * @param options
     */ on(event, listenerOrOptions, options) {
        // Determine if second arg is listener or options
        const isListenerFn = typeof listenerOrOptions === 'function';
        const listener = isListenerFn ? listenerOrOptions : undefined;
        const resolvedOptions = isListenerFn ? options : listenerOrOptions;
        if (!resolvedOptions?.once) {
            validateEvent('on', {
                event,
                listener
            });
            validateListener('on', {
                event,
                listener
            });
        }
        if (listener === undefined) {
            return new EventGenerator(this, event, resolvedOptions);
        }
        // Check if signal is already aborted
        const userSignal = resolvedOptions?.signal;
        if (userSignal?.aborted) {
            return ()=>{};
        }
        const { eventName, isRgx } = this.#eventInfo(event);
        const listenerMap = isRgx ? this.#rgxListenerMap : this.#listenerMap;
        this.#currentSpy({
            event,
            fn: resolvedOptions?.once ? 'once' : 'on',
            data: null,
            listener,
            context: this
        });
        const cbSet = listenerMap.get(eventName) || new Set([
            listener
        ]);
        if (cbSet && !cbSet.has(listener)) {
            cbSet.add(listener);
        }
        if (!listenerMap.has(eventName)) {
            listenerMap.set(eventName, cbSet);
        }
        this.#internalListener.dispatchEvent(new InternalEvent(InternalEvs.on, [
            event,
            listener
        ]));
        // Track if cleanup has been called to prevent double-cleanup
        let cleanedUp = false;
        const cleanup = ()=>{
            if (cleanedUp) return;
            cleanedUp = true;
            this.#currentSpy({
                event,
                fn: 'cleanup',
                data: null,
                listener,
                context: this
            });
            cbSet.delete(listener);
            // Remove empty Set from Map to prevent memory bloat
            if (cbSet.size === 0) {
                listenerMap.delete(eventName);
            }
            this.#internalListener.dispatchEvent(new InternalEvent(InternalEvs.off, [
                event,
                listener
            ]));
        };
        // Add abort listeners directly to avoid AbortSignal.any() memory retention
        // Each signal gets its own listener that we can remove independently
        if (userSignal) {
            const abortHandler = ()=>{
                cleanup();
                // Remove self from the other signal to prevent retention
                this.#instanceSignal.removeEventListener('abort', abortHandler);
            };
            // Listen on user signal
            userSignal.addEventListener('abort', abortHandler, {
                once: true
            });
            // Listen on instance signal
            this.#instanceSignal.addEventListener('abort', abortHandler, {
                once: true
            });
            // Wrap cleanup to also remove abort listeners
            const originalCleanup = cleanup;
            const wrappedCleanup = ()=>{
                originalCleanup();
                // Remove abort handlers to prevent them from firing after manual cleanup
                userSignal.removeEventListener('abort', abortHandler);
                this.#instanceSignal.removeEventListener('abort', abortHandler);
            };
            return wrappedCleanup;
        }
        return cleanup;
    }
    /**
     * Executes a callback once when the specified
     * event is emitted, or returns a promise that
     * resolves when the event is emitted
     */ once(event, listenerOrOptions, options) {
        // Determine if second arg is listener or options
        const isListenerFn = typeof listenerOrOptions === 'function';
        const listener = isListenerFn ? listenerOrOptions : undefined;
        const resolvedOptions = isListenerFn ? options : listenerOrOptions;
        validateEvent('once', {
            event,
            listener
        });
        validateListener('once', {
            event,
            listener
        });
        if (!listener) {
            const defer = new DeferredEvent();
            const userSignal = resolvedOptions?.signal;
            // If signal already aborted, reject immediately
            if (userSignal?.aborted) {
                defer.reject(userSignal.reason ?? new DOMException('Aborted', 'AbortError'));
                defer.promise.cleanup = ()=>{};
                return defer.promise;
            }
            // Mutable controller for managing abort listener lifecycle
            let promiseAbortCtrl;
            const cleanup = this.once(event, (data)=>{
                defer.resolve(data);
                promiseAbortCtrl?.abort();
            }, resolvedOptions);
            // Wire up signal to reject promise on abort
            if (userSignal) {
                promiseAbortCtrl = new AbortController();
                userSignal.addEventListener('abort', ()=>{
                    cleanup();
                    defer.reject(userSignal.reason ?? new DOMException('Aborted', 'AbortError'));
                }, {
                    signal: promiseAbortCtrl.signal
                });
            }
            defer.promise.cleanup = ()=>{
                cleanup();
                promiseAbortCtrl?.abort();
            };
            return defer.promise;
        }
        const self = this;
        const runOnce = function(...args) {
            const { eventName, isRgx } = self.#eventInfo(event);
            const listenerMap = isRgx ? self.#rgxListenerMap : self.#listenerMap;
            const fns = listenerMap.get(eventName);
            if (fns) {
                fns.delete(runOnce);
                if (fns.size === 0) listenerMap.delete(eventName);
            }
            listener.apply(self, args);
        };
        return this.on(event, runOnce, {
            once: true,
            ...resolvedOptions
        });
    }
    /**
     * Stop listening for an event
     * @param event
     * @param listener
     */ off(event, listener) {
        validateEvent('off', {
            event,
            listener
        });
        this.#currentSpy({
            event,
            fn: 'off',
            data: null,
            listener,
            context: this
        });
        const { eventName, isRgx, rgx } = this.#eventInfo(event);
        const matches = isRgx ? this.#withRgxMatchKeys(rgx) : [
            eventName
        ];
        matches.forEach((_ev)=>{
            const ev = _ev;
            this.#internalListener.dispatchEvent(new InternalEvent(InternalEvs.off, [
                ev,
                listener
            ]));
            if (listener) {
                const fns = this.#listenerMap.get(ev);
                if (fns) {
                    fns.delete(listener);
                    if (fns.size === 0) this.#listenerMap.delete(ev);
                }
                return;
            }
            ;
            this.#listenerMap.delete(ev);
        });
    }
    /** Emits an event */ emit(event, data) {
        validateEvent('emit', {
            event,
            data
        });
        if (this.#emitValidator) {
            this.#emitValidator(event, data, this);
        }
        this.#currentSpy({
            event,
            fn: 'emit',
            data,
            listener: null,
            context: this
        });
        const { eventName, isRgx, rgx } = this.#eventInfo(event);
        if (!isRgx) {
            const cbs = this.#listenerMap.get(eventName);
            const rgxCbs = this.#matchRgx(eventName);
            if (cbs) cbs.forEach((fn)=>fn.apply(this, [
                    data,
                    {
                        event,
                        listener: fn
                    }
                ]));
            if (rgxCbs) rgxCbs.forEach(({ func })=>func.apply(this, [
                    {
                        data,
                        event,
                        listener: func
                    }
                ]));
            this.#internalListener.dispatchEvent(new InternalEvent(InternalEvs.emit, [
                eventName,
                data
            ]));
            return;
        }
        const cbs = this.#matchStr(rgx);
        if (cbs) cbs.forEach(({ event, func })=>func.apply(this, [
                data,
                {
                    event,
                    listener: func
                }
            ]));
        this.#withRgxMatchKeys(rgx).forEach((ev)=>{
            this.#internalListener.dispatchEvent(new InternalEvent(InternalEvs.emit, [
                ev,
                data
            ]));
        });
    }
    clear() {
        this.#listenerMap.clear();
        this.#rgxListenerMap.clear();
        this.#internalListener.dispatchEvent(new InternalEvent(InternalEvs.clear, [
            'clear',
            null
        ]));
    }
    queue(event, process, options) {
        return new EventQueue({
            ...options,
            event,
            process,
            observer: this
        });
    }
    /**
     * Checks if an event key passes through the filter/exclude pipeline.
     * Filter narrows first, then exclude removes from that set.
     */ static #passesFilter(event, options) {
        if (!options) return true;
        const { filter, exclude } = options;
        if (filter) {
            const passes = filter.some((f)=>f instanceof RegExp ? f.test(event) : f === event);
            if (!passes) return false;
        }
        if (exclude) {
            const blocked = exclude.some((e)=>e instanceof RegExp ? e.test(event) : e === event);
            if (blocked) return false;
        }
        return true;
    }
    /**
     * Moves or copies listeners between two ObserverEngine instances.
     * Static methods on the same class can access # private fields directly.
     */ static #moveListeners(source, target, remove, options) {
        for (const [event, callbacks] of source.#listenerMap){
            if (!ObserverEngine.#passesFilter(event, options)) continue;
            const targetSet = target.#listenerMap.get(event) || new Set();
            for (const cb of callbacks){
                targetSet.add(cb);
            }
            target.#listenerMap.set(event, targetSet);
            if (remove) {
                source.#listenerMap.delete(event);
            }
        }
        for (const [event, callbacks] of source.#rgxListenerMap){
            if (!ObserverEngine.#passesFilter(event, options)) continue;
            const targetSet = target.#rgxListenerMap.get(event) || new Set();
            for (const cb of callbacks){
                targetSet.add(cb);
            }
            target.#rgxListenerMap.set(event, targetSet);
            if (remove) {
                source.#rgxListenerMap.delete(event);
            }
        }
    }
    /**
     * Transfers all matching listeners from source to target, removing
     * them from the source. Target's existing listeners are preserved.
     *
     * Useful when cloning an engine (e.g. FetchEngine) and carrying
     * over observability listeners to the new instance.
     *
     * @example
     *
     *     const old = new ObserverEngine();
     *     const next = new ObserverEngine();
     *
     *     old.on('ready', handler);
     *
     *     ObserverEngine.transfer(old, next);
     *     // `next` now owns the 'ready' listener; `old` has none
     *
     * @example
     *
     *     // Transfer only specific events
     *     ObserverEngine.transfer(old, next, {
     *         filter: ['ready', /^user\./]
     *     });
     */ static transfer(source, target, options) {
        ObserverEngine.#moveListeners(source, target, true, options);
    }
    /**
     * Copies all matching listeners from source to target without
     * removing them from the source. Target's existing listeners
     * are preserved.
     *
     * Useful when forking an engine so both instances react to the
     * same events independently.
     *
     * @example
     *
     *     const primary = new ObserverEngine();
     *     const mirror = new ObserverEngine();
     *
     *     primary.on('update', handler);
     *
     *     ObserverEngine.copy(primary, mirror);
     *     // Both engines now have the 'update' listener
     *
     * @example
     *
     *     // Copy everything except internal events
     *     ObserverEngine.copy(primary, mirror, {
     *         exclude: [/^__/]
     *     });
     */ static copy(source, target, options) {
        ObserverEngine.#moveListeners(source, target, false, options);
    }
}
